import React from 'react'
import {useTranslations} from 'next-intl'
import {css} from '@emotion/react'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import {ProcessStatus} from '../../../constants'
import delay from '../../../utils/delay'
import IndentOptionsSelect from '../../../components/modules/IndentOptionsSelect'
import ProcessStatusInfo from '../../../components/modules/ProcessStatusInfo'
import DataExportControl from '../../../components/modules/DataExportControl'
import InputResultPanelLayout from '../../../components/layouts/InputResultPanelLayout'
import useLangConfig from '../../../hooks/useLangConfig'
import TextEditor from '../../../components/modules/TextEditor'
import JSONViewButton from '../../../components/modules/JSONViewButton'


export default function CodeMergerLayout({lang, merge}) {
  const styles = {
    top: css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    `,

    inputControl: css`
      display: flex;
      justify-content: space-between;
    `,

    inputContent: css`
      height: 100%;
      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 8px;
    `,

    textBlock: css`
      flex: 1;
      width: 100%;

      display: flex;
      align-items: center;
    `,

    inputBottom: css`
      padding: 8px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
    `,

    bottomControlBar: css`
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      height: 36px;
    `,
  }

  const t = useTranslations()
  const langConfig = useLangConfig(lang)

  const [textInput1, setTextInput1] = React.useState(langConfig.defaultInput)
  const onTextInput1Change = (textInput) => {
    setTextInput1(textInput)
  }

  const [textInput2, setTextInput2] = React.useState(langConfig.defaultInput)
  const onTextInput2Change = (textInput) => {
    setTextInput2(textInput)
  }

  const [options, setOptions] = React.useState(langConfig.indent ? {
    useTabs: langConfig.indent.default === 'tab',
    tabWidth: langConfig.indent.default !== 'tab' ? langConfig.indent.default : undefined,
  } : {})
  const onIndentOptionsChange = (indentOptions) => {
    setOptions({...options, ...indentOptions})
  }

  const [outputResult, setOutputResult] = React.useState('')

  const [processStatus, setProcessStatus] = React.useState(ProcessStatus.Idle)
  React.useEffect(() => {
    if (textInput1) {
      let isSubscribed = true

      setProcessStatus(ProcessStatus.Processing)
      Promise.resolve()
        .then(() => delay(500))
        .then(() => merge(textInput1, textInput2, options))
        .then(outputResult => {
          if (isSubscribed) {
            setOutputResult(outputResult)
            setProcessStatus(ProcessStatus.Success)
          }
        })
        .catch(e => {
          if (isSubscribed) {
            setOutputResult(e.toString())
            setProcessStatus(ProcessStatus.Failed)
          }
        })
      return () => isSubscribed = false
    } else {
      setOutputResult('')
      setProcessStatus(ProcessStatus.Idle)
    }
  }, [textInput1, textInput2, merge, options])

  return (
    <InputResultPanelLayout
      inputContent={(
        <div css={styles.inputContent}>
          <div css={styles.textBlock}>
            <div css={css`
              flex: 1;
              height: 100%;
            `}>
              <TextEditor
                id="text-input-1"
                lang={lang}
                value={textInput1}
                onChange={onTextInput1Change}
                placeholder={t('text-editor.placeholder')}
                autoFocus={true}
              />
            </div>
            <ChevronRightIcon fontSize="large"/>
          </div>
          <div css={styles.textBlock}>
            <div css={css`
              flex: 1;
              height: 100%;
            `}>
              <TextEditor
                id="text-input-2"
                lang={lang}
                value={textInput2}
                onChange={onTextInput2Change}
                placeholder={t('text-editor.placeholder')}
              />
            </div>
            <ChevronRightIcon fontSize="large"/>
          </div>
        </div>
      )}
      inputBottom={(
        <div css={styles.inputBottom}>
          {langConfig.indent ? (
            <IndentOptionsSelect
              currentIndentOptions={options}
              onIndentOptionsChange={onIndentOptionsChange}
              supportTab={langConfig.indent.supportTab}
            />
          ) : (<div/>)}
          <ProcessStatusInfo id="process-status" processStatus={processStatus}/>
        </div>
      )}

      resultContent={(
        <TextEditor
          id="text-output"
          lang={lang}
          readOnly={true}
          loading={processStatus === ProcessStatus.Processing}
          value={outputResult instanceof Error ? outputResult.toString() : outputResult}

          status={
            lang === 'json' && <JSONViewButton text={outputResult}/>
          }
          extraControl={(
            <div css={styles.bottomControlBar}>
              <DataExportControl
                disabled={!outputResult || outputResult instanceof Error}
                data={outputResult}

                downloadButtonId={'download-button'}
                downloadFileExt={langConfig.fileExt}
              />
            </div>
          )}
        />
      )}
    />
  )
}

